/**
 * ADHD Reward Service
 * F078: Dopamine-friendly reward system with streak tracking,
 * celebration triggers, and encouraging messages.
 * All state persisted in localStorage.
 */

const STORAGE_KEY = 'adhd-rewards';

export type CelebrationLevel = 'micro' | 'small' | 'medium' | 'big';

export interface CelebrationEvent {
  level: CelebrationLevel;
  message: string;
  emoji: string;
}

interface RewardState {
  // Daily completions: { "2026-03-23": 5 }
  dailyCompletions: Record<string, number>;
  // Current streak (consecutive days with >= 1 completion)
  currentStreak: number;
  // Best streak ever
  bestStreak: number;
  // Total tasks completed all-time
  totalCompleted: number;
  // Last completion date (YYYY-MM-DD)
  lastCompletionDate: string;
  // Milestones reached (to avoid re-celebrating)
  milestonesReached: number[];
}

const DEFAULT_STATE: RewardState = {
  dailyCompletions: {},
  currentStreak: 0,
  bestStreak: 0,
  totalCompleted: 0,
  lastCompletionDate: '',
  milestonesReached: [],
};

// Encouraging messages by level
const MICRO_MESSAGES = [
  'Nice!',
  'Got it!',
  'One down!',
  'Check!',
  'Done!',
  'Boom!',
];

const SMALL_MESSAGES = [
  'Keep going, you\'re on a roll!',
  'That\'s real momentum!',
  'You\'re crushing it today!',
  'Look at you go!',
  'Three down, unstoppable!',
];

const MEDIUM_MESSAGES = [
  'Five tasks done! You\'re a machine today!',
  'Halfway through your day and killing it!',
  'Your focus is LOCKED IN right now!',
  'This is what flow state looks like!',
];

const BIG_MESSAGES = [
  'ALL FOCUS TASKS COMPLETE! You absolutely crushed today!',
  'Every single one. Done. You\'re incredible!',
  'Perfect day! Your future self is going to thank you!',
  'You did the hard thing. That takes real strength!',
];

const STREAK_MESSAGES: Record<number, string> = {
  3: '3-day streak! You\'re building a habit!',
  5: '5 days straight! Your brain is rewiring!',
  7: 'One full week! This is who you are now!',
  14: 'Two weeks of consistency! That\'s extraordinary!',
  30: '30-DAY STREAK! You\'re unstoppable!',
};

const MILESTONE_TOTALS = [10, 25, 50, 100, 250, 500, 1000];

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function todayStr(): string {
  return new Date().toISOString().split('T')[0];
}

function yesterdayStr(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}

class RewardService {
  private state: RewardState;
  private listeners: Array<(event: CelebrationEvent) => void> = [];

  constructor() {
    this.state = this.load();
    this.updateStreak();
  }

  private load(): RewardState {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...DEFAULT_STATE, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return { ...DEFAULT_STATE };
  }

  private save(): void {
    try {
      // Keep only last 60 days of daily completions to avoid bloat
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 60);
      const cutoffStr = cutoff.toISOString().split('T')[0];
      const trimmed: Record<string, number> = {};
      for (const [date, count] of Object.entries(this.state.dailyCompletions)) {
        if (date >= cutoffStr) trimmed[date] = count;
      }
      this.state.dailyCompletions = trimmed;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch { /* ignore */ }
  }

  private updateStreak(): void {
    const today = todayStr();
    const yesterday = yesterdayStr();
    const last = this.state.lastCompletionDate;

    if (last === today) {
      // Already completed today, streak is current
      return;
    } else if (last === yesterday) {
      // Completed yesterday but not today yet — streak holds but won't increment until today's first completion
      return;
    } else if (last && last < yesterday) {
      // Missed a day — reset streak
      this.state.currentStreak = 0;
      this.save();
    }
  }

  /** Subscribe to celebration events */
  onCelebration(callback: (event: CelebrationEvent) => void): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  private emit(event: CelebrationEvent): void {
    for (const l of this.listeners) {
      try { l(event); } catch { /* ignore */ }
    }
  }

  /**
   * Record a task completion. Returns a celebration event if warranted.
   * @param isFocusTask - true if this was a focus task (stronger celebration)
   * @param focusTasksTotal - total focus tasks for today
   * @param focusTasksCompleted - completed focus tasks count after this completion
   */
  recordCompletion(opts?: {
    isFocusTask?: boolean;
    focusTasksTotal?: number;
    focusTasksCompleted?: number;
  }): CelebrationEvent | null {
    const today = todayStr();
    const { isFocusTask, focusTasksTotal, focusTasksCompleted } = opts || {};

    // Update daily count
    const prevCount = this.state.dailyCompletions[today] || 0;
    const newCount = prevCount + 1;
    this.state.dailyCompletions[today] = newCount;
    this.state.totalCompleted++;

    // Update streak
    if (this.state.lastCompletionDate !== today) {
      const yesterday = yesterdayStr();
      if (this.state.lastCompletionDate === yesterday || this.state.currentStreak === 0) {
        this.state.currentStreak++;
      } else if (this.state.lastCompletionDate < yesterday) {
        this.state.currentStreak = 1;
      }
      this.state.lastCompletionDate = today;
    }

    if (this.state.currentStreak > this.state.bestStreak) {
      this.state.bestStreak = this.state.currentStreak;
    }

    this.save();

    // Determine celebration level
    let event: CelebrationEvent | null = null;

    // Check: all focus tasks complete?
    if (isFocusTask && focusTasksTotal && focusTasksCompleted && focusTasksCompleted >= focusTasksTotal) {
      event = { level: 'big', message: pick(BIG_MESSAGES), emoji: '🏆' };
    }
    // Check: streak milestone?
    else if (STREAK_MESSAGES[this.state.currentStreak] && newCount === 1) {
      event = { level: 'medium', message: STREAK_MESSAGES[this.state.currentStreak], emoji: '🔥' };
    }
    // Check: total milestone?
    else if (MILESTONE_TOTALS.includes(this.state.totalCompleted) &&
             !this.state.milestonesReached.includes(this.state.totalCompleted)) {
      this.state.milestonesReached.push(this.state.totalCompleted);
      this.save();
      event = { level: 'medium', message: `${this.state.totalCompleted} tasks completed all-time! You're building something great!`, emoji: '⭐' };
    }
    // Check: daily count milestones (3, 5, 10)
    else if (newCount === 5) {
      event = { level: 'medium', message: pick(MEDIUM_MESSAGES), emoji: '⚡' };
    } else if (newCount === 3) {
      event = { level: 'small', message: pick(SMALL_MESSAGES), emoji: '💪' };
    }
    // Default: micro celebration for every completion
    else {
      event = { level: 'micro', message: pick(MICRO_MESSAGES), emoji: '✓' };
    }

    if (event && event.level !== 'micro') {
      this.emit(event);
    }

    return event;
  }

  /** Record completing all steps in a task breakdown */
  recordBreakdownComplete(): CelebrationEvent {
    const event: CelebrationEvent = {
      level: 'medium',
      message: 'Every. Single. Step. You broke it down and knocked it out!',
      emoji: '🎯',
    };
    this.emit(event);
    return event;
  }

  getStreak(): number {
    this.updateStreak();
    return this.state.currentStreak;
  }

  getBestStreak(): number {
    return this.state.bestStreak;
  }

  getTodayCount(): number {
    return this.state.dailyCompletions[todayStr()] || 0;
  }

  getTotalCompleted(): number {
    return this.state.totalCompleted;
  }

  getState(): Readonly<RewardState> {
    return this.state;
  }
}

export const rewardService = new RewardService();
